import { DataProvider, Pagination, Selecting } from '@qualio/ui-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { QualioDocument } from '../../../../../api/model/document';
import { WorkspaceTabs } from '../WorkspaceV2';
import { BulkActions } from './BulkActions';

export type BulkActionsWrapperProps = {
  currentTab: WorkspaceTabs;
};

export const buildDocMap = (
  selectedIds: string[],
  qualioDocuments: QualioDocument[] | undefined,
  selectedDocumentsMap: Map<number, QualioDocument>,
) => {
  const newMap = new Map<number, QualioDocument>([]);
  if (qualioDocuments) {
    selectedIds.forEach((id) => {
      const idNumber = Number.parseInt(id);
      const qualioDocument =
        qualioDocuments.find((doc) => doc.id === idNumber) ||
        selectedDocumentsMap.get(idNumber);
      if (qualioDocument) {
        newMap.set(idNumber, qualioDocument);
      }
    });
  }
  return newMap;
};

export const BulkActionsWrapper = (props: BulkActionsWrapperProps) => {
  const selection = Selecting.useSelectionMaybe();
  const dataProvider = DataProvider.useDataProvider<QualioDocument>();

  const dataRef = useRef(dataProvider.data);
  dataRef.current = dataProvider.data;

  const selectionStr = useMemo(
    () =>
      Object.keys(selection?.selection ?? {})
        .filter((id) => selection?.selection?.[id])
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [selection?.selection],
  );

  const [selectedDocumentsMap, setSelectedDocumentsMap] = useState<
    Map<number, QualioDocument>
  >(new Map());

  useEffect(() => {
    const selectedIds = selectionStr ? selectionStr.split(',') : [];
    setSelectedDocumentsMap((prevMap) =>
      buildDocMap(selectedIds, dataRef.current, prevMap),
    );
  }, [selectionStr]);

  const selectedDocuments = useMemo(
    () => [...selectedDocumentsMap.values()],
    [selectedDocumentsMap],
  );

  const statuses = useMemo(
    () => selectedDocuments.map((document) => document.status_id),
    [selectedDocuments],
  );

  return (
    // We need to add the Pagination provider here, otherwise, data providers used for certain bulk actions (e.g. Assign Trainees) will try to set properties of the pagination provider on the main table (i.e. the count of the main table will show the number of users instead of the document count).
    <Pagination.Auto>
      <BulkActions
        currentTab={props.currentTab}
        selectedDocuments={selectedDocuments}
        statuses={statuses}
      />
    </Pagination.Auto>
  );
};
